import React, { useEffect } from "react";
import {
  TreeViewComponent,
  TreeViewModel,
} from "@syncfusion/ej2-react-navigations";
import {
  MaskedTextBoxComponent,
  MaskedTextBoxModel,
} from "@syncfusion/ej2-react-inputs";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import classNames from "classnames";
import { PdfTemplateDesignerModule } from "./usePdfTemplateDesigner";
import {
  usePdfTemplateDesignerExpressionToolbar,
  SearchResults,
  PdfTemplateDesignerExpressionToolbarModule,
} from "./usePdfTemplateDesignerExpressionToolbar";
import { LoadingV2 } from "../../Loading/LoadingV2";
import { PdfViewerComponent } from "@syncfusion/ej2-react-pdfviewer";
import { ExpressionData, AnnotationDefaults } from "../types/types";

export interface PdfTemplateDesignerExpressionsToolbarProps {
  disableFields: boolean;
  getTemplateFields?: () => Promise<ExpressionData[]>;
  templateDesignerModule?: PdfTemplateDesignerModule;
  pdfViewerRef: React.RefObject<PdfViewerComponent | null>;
  annotationDefaults?: AnnotationDefaults;
}

export const PdfTemplateDesignerExpressionsToolbar: React.FC<
  PdfTemplateDesignerExpressionsToolbarProps
> = (props) => {
  const [templateFields, setTemplateFields] = React.useState<
    ExpressionData[] | undefined
  >(undefined);
  const [searchResults, setSearchResults] = React.useState<SearchResults>();
  const [maskedTextBoxSettings, setMaskedTextBoxSettings] = React.useState<
    MaskedTextBoxModel & DefaultHtmlAttributes
  >();
  const [treeViewSettings, setTreeViewSettings] = React.useState<
    TreeViewModel & DefaultHtmlAttributes
  >();
  const [ready, setReady] = React.useState<boolean>(false);

  const maskedTextBoxRef = React.useRef<MaskedTextBoxComponent>(null);
  const treeViewRef = React.useRef<TreeViewComponent>(null);
  const expressionsToolbar = React.useRef<HTMLDivElement>(null);
  const expressionToolbarModuleRef = React.useRef<
    PdfTemplateDesignerExpressionToolbarModule | undefined
  >(undefined);

  // Use the new expression toolbar hook
  const expressionToolbarModule = usePdfTemplateDesignerExpressionToolbar(
    treeViewRef,
    maskedTextBoxRef,
    props.pdfViewerRef,
    undefined, // onPreviewAnnotations
    props.annotationDefaults
  );

  // Store the module in a ref to avoid dependency issues
  useEffect(() => {
    expressionToolbarModuleRef.current = expressionToolbarModule;
  }, [expressionToolbarModule]);

  // Setup
  useEffect(() => {
    // Fetch Fields
    fetchTemplateFields();
  }, []);

  const fetchTemplateFields = async () => {
    const data = await props.getTemplateFields?.();
    setTemplateFields(data);
  };

  useEffect(() => {
    if (
      templateFields &&
      templateFields.length > 0 &&
      expressionToolbarModuleRef.current
    ) {
      const [maskedTextBoxModel, treeViewModel] =
        expressionToolbarModuleRef.current.setup(templateFields);

      setMaskedTextBoxSettings(maskedTextBoxModel);
      setTreeViewSettings(treeViewModel);

      setReady(true);
    }
  }, [templateFields]);

  useEffect(() => {
    if (!treeViewRef.current) {
      return;
    }

    if (searchResults?.expand) {
      treeViewRef.current.expandAll();
    }
  }, [searchResults]);

  const handleMaskedTextBoxChange = () => {
    if (expressionToolbarModuleRef.current) {
      const results = expressionToolbarModuleRef.current.search();
      setSearchResults(results);
    }
  };

  return (
    <div
      className="expressions-toolbar"
      ref={expressionsToolbar}
    >
      {ready ? (
        <>
          <div className="search-expressions-input-container">
            <MaskedTextBoxComponent
              className="search-expressions-input"
              ref={maskedTextBoxRef}
              change={handleMaskedTextBoxChange}
              {...maskedTextBoxSettings}
              disabled={props.disableFields}
            />
          </div>
          <div
            className={classNames("fields-container", {
              "fields-disabled": props.disableFields,
            })}
          >
            <TreeViewComponent
              ref={treeViewRef}
              {...treeViewSettings}
              fullRowSelect={false}
              disabled={props.disableFields}
            />
          </div>
        </>
      ) : (
        <LoadingV2 />
      )}
    </div>
  );
};
